import { routing } from "@/i18n/routing";
import { type Locale } from "./lang";

/**
 * Records an *explicit* locale choice made by the user on this device.
 *
 * Deliberately separate from `NEXT_LOCALE`: the middleware rewrites that cookie
 * on every authenticated request, so it cannot distinguish "the user picked
 * this" from "we echoed back whatever we resolved". This cookie is written in
 * exactly one place — the language switcher's server action — and read
 * everywhere a redirect target is computed.
 *
 * Not `httpOnly`: it holds no secret, and client code benefits from reading it
 * without a server round-trip.
 */
export const LOCALE_PREF_COOKIE = "shield_locale_pref";

/**
 * Shared options for every locale cookie write.
 *
 * `sameSite: "lax"` matters: `"strict"` cookies are withheld on the top-level
 * cross-site navigation back from Auth0, which made the root layout render the
 * wrong `<html lang>` on the first post-login paint.
 */
export const LOCALE_COOKIE_OPTIONS = {
  path: "/",
  httpOnly: false,
  sameSite: "lax" as const,
  secure: process.env.NODE_ENV === "production"
};

/** Narrows arbitrary input (route params, cookies) to a supported locale. */
export const isLocale = (value: unknown): value is Locale =>
  typeof value === "string" &&
  (routing.locales as readonly string[]).includes(value);

/**
 * Locales the backend can persist, mirroring the Prisma `Language` enum in
 * `@shield-xyz/database` and `UpdateUserLanguageSchema` in `core`.
 *
 * Currently every locale in `routing.locales` is here, so nothing is
 * cookie-only. Keep it that way: a locale added to the switcher ahead of its
 * enum migration would 400 on `PATCH /users/language` and survive only in the
 * cookie — lost on a new browser, on cleared cookies, and on every other
 * device. List such a locale's absence here rather than letting the call fail.
 */
const BACKEND_PERSISTED_LOCALES: readonly string[] = ["en", "es", "pt"];

/** Whether `locale` can be written through to the backend / user record. */
export const isBackendPersistedLocale = (locale: string): boolean =>
  BACKEND_PERSISTED_LOCALES.includes(locale);

/**
 * Locales whose availability in the language switcher is gated by a feature
 * flag, keyed by locale.
 *
 * The value must exist as a `FeatureFlag` row in shield-core (see the admin
 * feature-flags page); flags read as off when absent, so an unseeded key hides
 * the locale everywhere. Locales without an entry are always offered — the
 * default locale must never be gated.
 */
export const LOCALE_FEATURE_FLAGS: Partial<Record<Locale, string>> = {
  pt: "locale_pt_enabled"
};

/**
 * The locales to offer as choices, given the current flag state.
 *
 * Gates the *choice*, not support: `/pt/*` keeps resolving and the catalog
 * still loads, so a bookmark or an existing `shield_locale_pref` cookie works
 * regardless. `currentLocale` is always included, so turning a flag off never
 * hides the language a user is currently reading — or strands them on it with
 * no way to switch back.
 *
 * Pure, so the gating rules are unit-testable without rendering the switcher.
 */
export const offeredLocalesFor = (
  currentLocale: string | undefined,
  isEnabled: (key: string) => boolean
): readonly Locale[] =>
  routing.locales.filter((locale) => {
    if (locale === currentLocale) return true;
    const flag = LOCALE_FEATURE_FLAGS[locale];
    return !flag || isEnabled(flag);
  });

/**
 * Resolves the locale a request should be served in.
 *
 * Precedence: explicit cookie choice → the user record → the default.
 *
 * The cookie outranks the user record so a deliberate switch takes effect on
 * this device immediately, without waiting on the round-trip that syncs it to
 * the user record — and still wins if that sync fails. (It originally existed
 * because the record could not hold every locale at all; that is no longer
 * true, but same-device immediacy is reason enough to keep it.) Since the
 * cookie is only ever written on a deliberate switch, a user who never touches
 * the switcher resolves through their stored preference.
 *
 * Pure by construction (no `next/headers`), so it is safe in edge middleware
 * and directly unit-testable.
 */
export const resolvePreferredLocale = ({
  cookieValue,
  sessionLanguage
}: {
  cookieValue?: string | null;
  sessionLanguage?: string | null;
}): Locale => {
  if (isLocale(cookieValue)) return cookieValue;
  if (isLocale(sessionLanguage)) return sessionLanguage;
  return routing.defaultLocale;
};
